import React, { useEffect } from 'react'
import { toastError, toastSuccess } from '../../utils/toastHelper'
import { formatDateDisplay } from '../../utils/dateHelpers'
import { useTheme } from '../../hooks/useTheme'

interface QRValidationData {
  valid: boolean
  message: string
  reserva_id?: string
  cliente_nombre?: string
  espacio_nombre?: string
  fecha_entrada?: string
  fecha_salida?: string
}

interface QRValidationResultProps {
  data: QRValidationData | null
  isLoading: boolean
  onClose: () => void
}

const QRValidationResult: React.FC<QRValidationResultProps> = ({
  data,
  isLoading,
  onClose,
}) => {
  const { colors } = useTheme()

  // Show toast notification when validation result changes
  useEffect(() => {
    if (data) {
      if (data.valid) {
        toastSuccess(data.message)
      } else {
        toastError(data.message)
      }
    }
  }, [data])

  if (!data) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header con color según estado */}
        <div
          className="px-6 py-8 text-center"
          style={{
            backgroundColor: data.valid
              ? '#D1FAE5' // Verde suave
              : '#FEE2E2', // Rojo suave
          }}
        >
          <div className="text-6xl mb-4">
            {data.valid ? '✅' : '❌'}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {data.valid ? '¡Reserva Válida!' : 'Reserva Inválida'}
          </h2>
          <p
            className="text-sm font-medium"
            style={{
              color: data.valid ? '#059669' : '#DC2626',
            }}
          >
            {data.message}
          </p>
        </div>

        {/* Contenido - Solo si es válida */}
        {data.valid && (
          <div className="px-6 py-6 bg-gray-50">
            <div className="space-y-4">
              {/* Cliente */}
              {data.cliente_nombre && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                    Cliente
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {data.cliente_nombre}
                  </p>
                </div>
              )}

              {/* Espacio */}
              {data.espacio_nombre && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                    Espacio
                  </p>
                  <p className="text-lg font-bold" style={{ color: colors.primary }}>
                    {data.espacio_nombre}
                  </p>
                </div>
              )}

              {/* Fechas */}
              {data.fecha_entrada && data.fecha_salida && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                      Entrada
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {formatDateDisplay(data.fecha_entrada)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                      Salida
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {formatDateDisplay(data.fecha_salida)}
                    </p>
                  </div>
                </div>
              )}

              {/* ID Reserva */}
              {data.reserva_id && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                    Reserva ID
                  </p>
                  <p className="text-xs font-mono text-gray-700 break-all">
                    {data.reserva_id}
                  </p>
                </div>
              )}

              {/* Mensaje de bienvenida */}
              <div
                className="rounded-lg p-4 text-center"
                style={{
                  backgroundColor: `${colors.primary}20`,
                  borderLeft: `4px solid ${colors.primary}`,
                }}
              >
                <p
                  className="font-semibold"
                  style={{ color: colors.primary }}
                >
                  ¡Bienvenido al Camping!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contenido - Si es inválida */}
        {!data.valid && (
          <div className="px-6 py-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-900 font-semibold mb-2">
                💡 Si tienes problemas con la cámara:
              </p>
              <p className="text-xs text-blue-800">
                Puedes ingresar manualmente el código QR en el campo de texto disponible en esta página.
              </p>
            </div>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="px-6 py-8 bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Validando...</p>
            </div>
          </div>
        )}

        {/* Botón cerrar */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 rounded-lg font-medium text-white transition-colors"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default QRValidationResult
